import React from 'react';
import { Milestone, Contract } from '../../types/contract';
import { MilestoneCard } from './MilestoneCard';
import { sortMilestones } from '../../utils/contractHelpers';
import { CheckCircle, Circle } from 'lucide-react';

interface PendingExtension {
  amendmentId: string;
  proposedBy: string;
  oldCompletionDate: number;
  newCompletionDate: number;
  reason?: string;
  createdAt: number;
}

interface MilestoneTimelineProps {
  milestones: Milestone[];
  contract: Contract;
  currentUserId: string;
  onStartMilestone?: (milestoneId: string) => void;
  onSubmitDeliverable?: (milestoneId: string) => void;
  onApproveMilestone?: (milestoneId: string) => void;
  onRequestRevision?: (milestoneId: string) => void;
  onCancelMilestone?: (milestoneId: string) => void;
  pendingExtensionsByMilestoneId?: Record<string, PendingExtension>;
  onRequestExtension?: (milestoneId: string) => void;
  onApproveExtension?: (amendmentId: string) => void;
  onRejectExtension?: (amendmentId: string) => void;
}

export const MilestoneTimeline: React.FC<MilestoneTimelineProps> = ({
  milestones,
  contract,
  currentUserId,
  onStartMilestone,
  onSubmitDeliverable,
  onApproveMilestone,
  onRequestRevision,
  onCancelMilestone,
  pendingExtensionsByMilestoneId,
  onRequestExtension,
  onApproveExtension,
  onRejectExtension,
}) => {
  const sortedMilestones = sortMilestones(milestones);

  if (sortedMilestones.length === 0) {
    return (
      <div className="text-center py-12">
        <Circle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-600">此合同暂无里程碑</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {sortedMilestones.map((milestone, index) => {
        const isLast = index === sortedMilestones.length - 1;
        const isCompleted = milestone.status === 'COMPLETED';
        const isCancelled = milestone.status === 'CANCELLED';

        return (
          <div key={milestone.milestone_id} className="relative">
            {/* Timeline Line */}
            {!isLast && (
              <div
                className={`absolute left-4 top-14 bottom-0 w-0.5 -mb-6 ${
                  isCompleted ? 'bg-green-300' : 'bg-slate-200'
                }`}
                style={{ height: 'calc(100% + 24px)' }}
              />
            )}

            {/* Timeline Node */}
            <div className="absolute left-0 top-6 z-10">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-4 ${
                  isCompleted
                    ? 'bg-green-500 border-green-100'
                    : isCancelled
                    ? 'bg-red-500 border-red-100'
                    : 'bg-white border-slate-200'
                }`}
              >
                {isCompleted && <CheckCircle className="w-4 h-4 text-white" />}
                {!isCompleted && !isCancelled && (
                  <span className="text-xs font-semibold text-slate-600">
                    {milestone.order_index}
                  </span>
                )}
              </div>
            </div>

            {/* Milestone Card */}
            <div className="ml-16">
              <MilestoneCard
                milestone={milestone}
                contract={contract}
                currentUserId={currentUserId}
                onStartMilestone={onStartMilestone}
                onSubmitDeliverable={onSubmitDeliverable}
                onApproveMilestone={onApproveMilestone}
                onRequestRevision={onRequestRevision}
                onCancelMilestone={onCancelMilestone}
                pendingExtension={pendingExtensionsByMilestoneId?.[milestone.milestone_id]}
                onRequestExtension={onRequestExtension}
                onApproveExtension={onApproveExtension}
                onRejectExtension={onRejectExtension}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
